import { Button, Image, Space, Tag, Tooltip } from "antd";
import type { TableColumnsType } from "antd";
import {
    DeleteOutlined,
    EditOutlined,
    EyeOutlined,
    PictureOutlined,
    PlayCircleOutlined,
    StopOutlined,
} from "@ant-design/icons";
import type { TFunction } from "i18next";

import type {
    ValidationDataset,
    ValidationDatasetSample,
    ValidationDatasetVersion,
} from "../../../types/modelValidation";
import { localeService } from "../../../services/localeService";
import NonDismissiblePopconfirm from "../../../components/common/NonDismissiblePopconfirm";
import { ValidationAssetStatusTag } from "./ValidationAssetStatusTag";

interface ValidationDatasetColumnsOptions {
    canEvaluate: boolean;
    onDelete: (dataset: ValidationDataset) => Promise<void>;
    onEdit: (dataset: ValidationDataset) => void;
    onManageImages: (dataset: ValidationDataset) => void;
    onSetDisabled: (dataset: ValidationDataset, disabled: boolean) => void;
    t: TFunction;
}

export const buildValidationDatasetColumns = ({
    canEvaluate,
    onDelete,
    onEdit,
    onManageImages,
    onSetDisabled,
    t,
}: ValidationDatasetColumnsOptions): TableColumnsType<ValidationDataset> => [
    {
        title: t("admin:modelValidation.columns.name"),
        dataIndex: "name",
        ellipsis: true,
        width: 280,
    },
    {
        title: t("admin:modelValidation.columns.description"),
        dataIndex: "description",
        ellipsis: { showTitle: false },
        render: (value?: string | null) => value || "-",
    },
    {
        title: t("admin:modelValidation.columns.status"),
        width: 90,
        render: (_, record) => (
            <ValidationAssetStatusTag status={record.status} />
        ),
    },
    {
        title: t("admin:modelValidation.columns.createdAt"),
        dataIndex: "created_at",
        render: (value: string) => localeService.formatDateTime(value),
        width: 160,
    },
    {
        title: t("admin:modelValidation.columns.actions"),
        fixed: "right",
        width: 250,
        render: (_, record) => {
            const metadataEditable =
                canEvaluate &&
                !record.is_read_only &&
                record.status !== "disabled";
            const imagesEditable =
                canEvaluate &&
                !record.is_read_only &&
                !record.is_locked &&
                record.status !== "disabled";
            const lifecycleEditable =
                canEvaluate &&
                !record.is_read_only &&
                ["published", "disabled"].includes(record.status);
            const disabled = record.status === "published";
            return (
                <Space size={[4, 4]} wrap>
                    {metadataEditable ? (
                        <Button
                            aria-label={t("admin:modelValidation.actions.edit")}
                            icon={<EditOutlined />}
                            onClick={() => onEdit(record)}
                            size="small"
                            type="link"
                        >
                            {t("admin:modelValidation.actions.edit")}
                        </Button>
                    ) : null}
                    <Button
                        aria-label={t(
                            `admin:modelValidation.actions.${
                                imagesEditable ? "manageImages" : "viewImages"
                            }`,
                        )}
                        icon={
                            imagesEditable ? (
                                <PictureOutlined />
                            ) : (
                                <EyeOutlined />
                            )
                        }
                        onClick={() => onManageImages(record)}
                        size="small"
                        type="link"
                    >
                        {t(
                            `admin:modelValidation.actions.${
                                imagesEditable ? "manageImages" : "viewImages"
                            }`,
                        )}
                    </Button>
                    {canEvaluate &&
                    !record.is_read_only &&
                    record.status === "draft" ? (
                        <NonDismissiblePopconfirm
                            cancelText={t(
                                "admin:modelValidation.actions.cancel",
                            )}
                            description={t(
                                "admin:modelValidation.confirmations.deleteDatasetDescription",
                            )}
                            okText={t("admin:modelValidation.actions.confirm")}
                            onConfirm={() => onDelete(record)}
                            title={t(
                                "admin:modelValidation.confirmations.deleteDatasetTitle",
                            )}
                        >
                            <Button
                                aria-label={t(
                                    "admin:modelValidation.actions.delete",
                                )}
                                danger
                                icon={<DeleteOutlined />}
                                size="small"
                                type="link"
                            >
                                {t("admin:modelValidation.actions.delete")}
                            </Button>
                        </NonDismissiblePopconfirm>
                    ) : null}
                    {lifecycleEditable ? (
                        <Button
                            aria-label={t(
                                `admin:modelValidation.actions.${disabled ? "disable" : "restore"}`,
                            )}
                            danger={disabled}
                            icon={
                                disabled ? (
                                    <StopOutlined />
                                ) : (
                                    <PlayCircleOutlined />
                                )
                            }
                            onClick={() => onSetDisabled(record, disabled)}
                            size="small"
                            type="link"
                        >
                            {t(
                                `admin:modelValidation.actions.${disabled ? "disable" : "restore"}`,
                            )}
                        </Button>
                    ) : null}
                </Space>
            );
        },
    },
];

interface ValidationDatasetSampleColumnsOptions {
    canEvaluate: boolean;
    onAnnotate: (sample: ValidationDatasetSample) => void;
    onDelete: (sample: ValidationDatasetSample) => Promise<void>;
    selectedVersion: ValidationDatasetVersion | null;
    t: TFunction;
}

export const buildValidationDatasetSampleColumns = ({
    canEvaluate,
    onAnnotate,
    onDelete,
    selectedVersion,
    t,
}: ValidationDatasetSampleColumnsOptions): TableColumnsType<ValidationDatasetSample> => [
    {
        title: t("admin:modelValidation.columns.preview"),
        dataIndex: "image_url",
        width: 92,
        render: (value: string | null | undefined, record) =>
            value ? (
                <Image
                    alt={record.filename}
                    height={52}
                    loading="lazy"
                    preview={{ mask: null, maskClosable: false }}
                    src={value}
                    style={{ objectFit: "cover" }}
                    width={72}
                />
            ) : (
                "-"
            ),
    },
    {
        title: t("admin:modelValidation.columns.order"),
        dataIndex: "sample_order",
        width: 72,
    },
    {
        title: t("admin:modelValidation.columns.image"),
        dataIndex: "filename",
        ellipsis: true,
    },
    {
        title: t("admin:modelValidation.columns.source"),
        dataIndex: "source_type",
        width: 100,
        render: (value: string) =>
            t(`admin:modelValidation.datasets.source.${value}`),
    },
    {
        title: t("admin:modelValidation.columns.reviewed"),
        dataIndex: "annotation_reviewed",
        width: 100,
        render: (value: boolean) => (
            <Tag color={value ? "success" : "warning"}>
                {t(
                    `admin:modelValidation.datasets.${value ? "reviewed" : "pendingReview"}`,
                )}
            </Tag>
        ),
    },
    {
        title: t("admin:modelValidation.columns.actions"),
        width: 150,
        fixed: "right",
        render: (_, record) => {
            const editable = selectedVersion?.status === "draft";
            const action = editable ? "annotate" : "view";
            return (
                <Space size="small">
                    <Tooltip
                        title={t(`admin:modelValidation.actions.${action}`)}
                    >
                        <Button
                            aria-label={t(
                                `admin:modelValidation.actions.${action}`,
                            )}
                            icon={editable ? <EditOutlined /> : <EyeOutlined />}
                            onClick={() => onAnnotate(record)}
                        />
                    </Tooltip>
                    {canEvaluate && selectedVersion?.status === "draft" ? (
                        <NonDismissiblePopconfirm
                            title={t(
                                "admin:modelValidation.datasets.deleteSampleConfirm",
                            )}
                            onConfirm={() => onDelete(record)}
                        >
                            <Button
                                aria-label={t(
                                    "admin:modelValidation.actions.delete",
                                )}
                                danger
                                icon={<DeleteOutlined />}
                            />
                        </NonDismissiblePopconfirm>
                    ) : null}
                </Space>
            );
        },
    },
];
